import type { AgentInfo, RelayToTab, TabInfo, TabProject, TabToRelay } from "idfweb-relay/protocol";
import { MCP_PATH, RELAY_PATH } from "idfweb-relay/protocol";
import { storedValue } from "idfweb-settings";
import type { ToolRegistry } from "idfweb-tools";

import "./agent.css";

/**
 * The page's connection to the relay, through which an MCP client calls the
 * page's tools.
 *
 * The page generates the token of its MCP URL and keeps it in this browser,
 * so the URL an agent is configured with stays valid across reloads. The
 * relay receives only the token's hash from the page. Every tab of the
 * browser profile shares the token; the relay tells them apart by the project
 * each has open and by when the user last focused each.
 */

const storedToken = storedValue("idfweb.agent.token");
const storedEnabled = storedValue("idfweb.agent.enabled");

const RETRY_FIRST_MS = 1000;
const RETRY_MAX_MS = 30_000;

export type AgentState =
    | { kind: "off" }
    | { kind: "connecting" }
    /**
     * `agent` is the MCP client using this tab, once one has made a request;
     * `chosen` is whether calls that choose no tab come to this one.
     */
    | { kind: "connected"; agent: AgentInfo | undefined; chosen: boolean }
    | { kind: "retrying"; inMs: number }
    | { kind: "refused"; reason: string };

export class AgentLink {
    readonly #registry: ToolRegistry;
    readonly #listeners = new Set<() => void>();
    #state: AgentState = { kind: "off" };
    #socket: WebSocket | undefined;
    #retryMs = RETRY_FIRST_MS;
    #retryTimer: ReturnType<typeof setTimeout> | undefined;
    /** What the relay is told about this tab. */
    #tab: TabInfo = { focused: document.hasFocus() ? Date.now() : 0 };

    constructor(registry: ToolRegistry) {
        this.#registry = registry;
        const focused = () => this.#report({ ...this.#tab, focused: Date.now() });
        window.addEventListener("focus", focused);
        document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "visible" && document.hasFocus()) focused();
        });
        if (storedEnabled.get() === "1" && storedToken.get() !== undefined) this.#open();
    }

    /** The kept project open in this tab changed, or there is none. */
    setProject(project: TabProject | undefined): void {
        const { focused } = this.#tab;
        this.#report(project === undefined ? { focused } : { focused, project });
    }

    get state(): AgentState {
        return this.#state;
    }

    /** The URL to configure an MCP client with, once a token exists. */
    get mcpUrl(): string | undefined {
        const token = storedToken.get();
        return token === undefined ? undefined : new URL(`${MCP_PATH}/${token}`, document.baseURI).href;
    }

    onChange(listener: () => void): () => void {
        this.#listeners.add(listener);
        return () => this.#listeners.delete(listener);
    }

    connect(): void {
        if (storedToken.get() === undefined) storedToken.set(newToken());
        storedEnabled.set("1");
        this.#close();
        this.#open();
    }

    disconnect(): void {
        storedEnabled.set(undefined);
        this.#close();
        this.#setState({ kind: "off" });
    }

    /** Replace the token. The previous MCP URL stops working. */
    renew(): void {
        storedToken.set(newToken());
        if (this.#state.kind === "off") this.#emit();
        else this.connect();
    }

    #report(tab: TabInfo): void {
        this.#tab = tab;
        // Also before the registration, which then carries the same.
        const socket = this.#socket;
        if (socket?.readyState === WebSocket.OPEN) send(socket, { type: "tab", tab });
    }

    #open(): void {
        this.#setState({ kind: "connecting" });
        const url = new URL(RELAY_PATH, document.baseURI);
        url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
        const socket = new WebSocket(url);
        this.#socket = socket;

        socket.onopen = () => {
            void tokenHash(storedToken.get() ?? "").then((hash) =>
                send(socket, { type: "register", tokenHash: hash, tab: this.#tab }),
            );
        };
        socket.onmessage = (event: MessageEvent<string>) => {
            const message = JSON.parse(event.data) as RelayToTab;
            switch (message.type) {
                case "registered":
                    this.#retryMs = RETRY_FIRST_MS;
                    this.#setState({ kind: "connected", agent: undefined, chosen: false });
                    return;
                case "agent":
                    if (this.#state.kind === "connected") {
                        this.#setState({ kind: "connected", agent: message.agent, chosen: message.chosen });
                    }
                    return;
                case "refused":
                    this.#close();
                    this.#setState({ kind: "refused", reason: message.reason });
                    return;
                case "call":
                    void this.#registry
                        .call(message.name, message.args, {
                            progress: (text) => send(socket, { type: "progress", id: message.id, message: text }),
                        })
                        .then((result) => send(socket, { type: "result", id: message.id, result }));
                    return;
            }
        };
        // After a lost connection, and after the hub's sign-in expired, which
        // refuses the WebSocket before it opens.
        socket.onclose = () => {
            if (this.#socket !== socket) return;
            this.#socket = undefined;
            const inMs = this.#retryMs;
            this.#retryMs = Math.min(inMs * 2, RETRY_MAX_MS);
            this.#retryTimer = setTimeout(() => this.#open(), inMs);
            this.#setState({ kind: "retrying", inMs });
        };
    }

    #close(): void {
        clearTimeout(this.#retryTimer);
        const socket = this.#socket;
        this.#socket = undefined;
        socket?.close();
    }

    #setState(state: AgentState): void {
        this.#state = state;
        this.#emit();
    }

    #emit(): void {
        for (const listener of this.#listeners) listener();
    }
}

function send(socket: WebSocket, message: TabToRelay): void {
    socket.send(JSON.stringify(message));
}

/** The toolbar button that shows the connection and opens its dialog. */
export function createAgentButton(link: AgentLink): HTMLButtonElement {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "idfweb-device-button idfweb-icon-button idfweb-agent-button codicon codicon-robot";
    const render = () => {
        const state = link.state;
        button.classList.toggle("idfweb-agent-connected", state.kind === "connected");
        button.title = `AI agent: ${describe(state)}`;
        button.setAttribute("aria-label", button.title);
    };
    link.onChange(render);
    render();
    button.addEventListener("click", () => showDialog(button.closest(".idfweb-device") ?? document.body, link));
    return button;
}

function showDialog(root: Element, link: AgentLink): void {
    const dialog = document.createElement("dialog");
    dialog.className = "idfweb-device-dialog idfweb-agent-dialog";

    const render = () => {
        const url = link.mcpUrl;
        const on = link.state.kind !== "off";
        const children: HTMLElement[] = [
            paragraph(
                "An AI agent such as Claude Code can build this project, read and edit its files, " +
                    "flash the board and read its serial output through an MCP URL.",
            ),
            paragraph(`Status: ${describe(link.state)}.`),
        ];
        if (link.state.kind === "connected") {
            children.push(
                paragraph(
                    link.state.chosen
                        ? "Agents use this tab unless they ask for another tab's project."
                        : "Agents use the idfweb tab focused last, or the tab whose project they ask for.",
                ),
            );
        }
        if (url !== undefined) {
            children.push(
                paragraph("MCP URL:"),
                copyField(url),
                paragraph("For Claude Code:"),
                copyField(`claude mcp add --transport http idfweb ${url}`),
                paragraph(
                    "Anyone with this URL can use the tools while this tab is connected. " +
                        "New URL makes the previous one stop working.",
                ),
            );
        }
        const buttons = row(
            ...(url === undefined ? [] : [dialogButton("New URL", () => link.renew())]),
            on
                ? dialogButton("Disconnect", () => link.disconnect())
                : dialogButton("Connect", () => link.connect(), true),
            dialogButton("Close", () => dialog.close()),
        );
        buttons.className = "idfweb-device-dialog-buttons";
        dialog.replaceChildren(...children, buttons);
    };

    const unsubscribe = link.onChange(render);
    render();
    dialog.addEventListener("close", () => {
        unsubscribe();
        dialog.remove();
    });
    root.append(dialog);
    dialog.showModal();
}

function describe(state: AgentState): string {
    switch (state.kind) {
        case "off":
            return "not connected";
        case "connecting":
            return "connecting";
        case "connected":
            if (state.agent === undefined) return "connected, no agent has used this tab yet";
            return `connected, used by ${agentName(state.agent)}`;
        case "retrying":
            return `the connection was lost, retrying in ${Math.round(state.inMs / 1000)} s`;
        case "refused":
            return `the relay refused this tab: ${state.reason}`;
    }
}

function agentName(agent: AgentInfo): string {
    return agent.name === "" ? "an agent" : `${agent.name} ${agent.version}`.trimEnd();
}

/** A read-only text field with a button that copies its value. */
function copyField(value: string): HTMLDivElement {
    const field = document.createElement("input");
    field.readOnly = true;
    field.value = value;
    field.className = "idfweb-agent-copyable";
    field.addEventListener("focus", () => field.select());
    return row(
        field,
        dialogButton("Copy", () => void navigator.clipboard.writeText(value)),
    );
}

function paragraph(text: string): HTMLParagraphElement {
    const element = document.createElement("p");
    element.textContent = text;
    return element;
}

function row(...children: HTMLElement[]): HTMLDivElement {
    const element = document.createElement("div");
    element.className = "idfweb-agent-row";
    element.append(...children);
    return element;
}

function dialogButton(label: string, onClick: () => void, primary = false): HTMLButtonElement {
    const element = document.createElement("button");
    element.type = "button";
    element.className = primary ? "idfweb-device-button idfweb-device-button-primary" : "idfweb-device-button";
    element.textContent = label;
    element.addEventListener("click", onClick);
    return element;
}

/** 32 random bytes in base64url. */
function newToken(): string {
    const bytes = crypto.getRandomValues(new Uint8Array(32));
    return btoa(String.fromCharCode(...bytes))
        .replaceAll("+", "-")
        .replaceAll("/", "_")
        .replace(/=+$/, "");
}

async function tokenHash(token: string): Promise<string> {
    const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(token));
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}
